// src/pages/ResourcesPage.js - Resources page component
import React from 'react';
import styled from 'styled-components';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin: var(--spacing-xl) 0 var(--spacing-lg);
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
`;

const ContactCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  
  .icon {
    font-size: 1.5rem;
    color: var(--primary-color);
  }
`;

const Glossary = styled.div`
  margin-bottom: var(--spacing-xxl);
`;

const GlossaryItem = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const GlossaryTerm = styled.h4`
  font-size: 1.125rem;
  margin-bottom: var(--spacing-xs);
  color: var(--primary-color);
`;

const GlossaryDefinition = styled.p`
  color: var(--text-color);
`;

const ResourcesPage = () => {
  return (
    <>
      <PageTitle>Resources</PageTitle>
      
      <section id="contact">
        <SectionTitle>Contact Information</SectionTitle>
        <p>If you have questions about PHI handling or need assistance, please contact the following departments:</p>
        
        <ResourcesGrid>
          <ContactCard title="IT Security Team">
            <ContactInfo>
              <div className="icon">📧</div>
              <div>
                <strong>Email:</strong><br />
                <a href="mailto:itsupport@aranscia.com">itsupport@aranscia.com</a>
              </div>
            </ContactInfo>
            <p>Contact for technical issues related to email encryption, secure messaging, or access problems.</p>
          </ContactCard>
          
          <ContactCard title="Compliance Office">
            <ContactInfo>
              <div className="icon">📧</div>
              <div>
                <strong>Email:</strong><br />
                <a href="mailto:compliance@aranscia.com">compliance@aranscia.com</a>
              </div>
            </ContactInfo>
            <p>Contact for questions about HIPAA policies, PHI handling procedures, or to report potential violations.</p>
          </ContactCard>
          
          <ContactCard title="Training Department">
            <ContactInfo>
              <div className="icon">📧</div>
              <div>
                <strong>Email:</strong><br />
                <a href="mailto:training@aranscia.com">training@aranscia.com</a>
              </div>
            </ContactInfo>
            <p>Contact to schedule additional training sessions or request specialized PHI handling training.</p>
          </ContactCard>
        </ResourcesGrid>
      </section>
      
      <section id="glossary">
        <SectionTitle>Glossary of Terms</SectionTitle>
        <Glossary>
          <GlossaryItem>
            <GlossaryTerm>Protected Health Information (PHI)</GlossaryTerm>
            <GlossaryDefinition>
              Any individually identifiable health information that is transmitted or maintained in any form or medium by a covered entity or business associate.
            </GlossaryDefinition>
          </GlossaryItem>
          
          <GlossaryItem>
            <GlossaryTerm>HIPAA</GlossaryTerm>
            <GlossaryDefinition>
              The Health Insurance Portability and Accountability Act of 1996, which established national standards for the protection of certain health information.
            </GlossaryDefinition>
          </GlossaryItem>
          
          <GlossaryItem>
            <GlossaryTerm>Encryption</GlossaryTerm>
            <GlossaryDefinition>
              The process of converting information into a code to prevent unauthorized access, ensuring that only individuals with the decryption key can access the information.
            </GlossaryDefinition>
          </GlossaryItem>
          
          <GlossaryItem>
            <GlossaryTerm>Covered Entity</GlossaryTerm>
            <GlossaryDefinition>
              A health plan, healthcare clearinghouse, or healthcare provider who electronically transmits health information in connection with certain transactions.
            </GlossaryDefinition>
          </GlossaryItem>
          
          <GlossaryItem>
            <GlossaryTerm>Business Associate</GlossaryTerm>
            <GlossaryDefinition>
              A person or organization that performs certain functions or activities involving the use or disclosure of PHI on behalf of a covered entity.
            </GlossaryDefinition>
          </GlossaryItem>
          
          <GlossaryItem>
            <GlossaryTerm>Secure Email Portal</GlossaryTerm>
            <GlossaryDefinition>
              A web-based platform that allows for the secure exchange of sensitive information, requiring authentication to access encrypted messages.
            </GlossaryDefinition>
          </GlossaryItem>
        </Glossary>
      </section>
      
      <section id="downloads">
        <SectionTitle>Resources</SectionTitle>
        <ResourcesGrid>
          <Card 
            title="Email Encryption Guide"
            description="Step-by-step guide for encrypting emails containing PHI."
            footer={
              <Button 
                variant="tertiary" 
                size="small" 
                as="a" 
                href="/resources/email-encryption-guide.html" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Guide
              </Button>
            }
          />
          
          <Card 
            title="PHI Identification Checklist"
            description="Detailed checklist for identifying protected health information."
            footer={
              <Button 
                variant="tertiary" 
                size="small" 
                as="a" 
                href="/resources/phi-identification-checklist.html" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Guide
              </Button>
            }
          />
          
          <Card 
            title="Quick Reference Card"
            description="Pocket-sized reference card with key PHI email guidelines."
            footer={
              <Button 
                variant="tertiary" 
                size="small" 
                as="a" 
                href="/resources/phi-quick-reference-card.html" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Guide
              </Button>
            }
          />
        </ResourcesGrid>
      </section>
    </>
  );
};

export default ResourcesPage; 